import { defineStore } from 'pinia';
import type { IKeyString, IModalType, IResponseDetail } from '@coreInterfaces/interface';

export const useActionStore = defineStore('action-store', {
    state: () => {
        return {
            components: {} as {
                [key: string]: any;
            },
            forms: {
                id: '' as string,
                isError: false as boolean,
                error: {} as Object,
                result: {} as Object,
                rules: {} as Record<string, Record<string, any>>
            },
            lists: {
                isDownloaded: false as boolean,
                isLoaded: false as boolean
            },
            modals: {
                default: {
                    isShow: false
                }
            } as IModalType,
            response: {
                action: '',
                status: false,
                message: '',
                data: null
            } as IResponseDetail,
            selopts: {
                isLoaded: false as boolean,
                isRefresh: false as boolean
            },
            urls: {} as any
        };
    },

    getters: {
        getComponentAction:
            (state) =>
            (name: string): Object => {
                return state.components[name];
            },

        getFormError: (state) => (): Object => {
            return state.forms.error;
        },

        getFormID: (state) => (): string => {
            return state.forms.id;
        },

        getListDownloaded: (state) => (): boolean => {
            return state.lists.isDownloaded;
        },

        getListLoaded: (state) => (): boolean => {
            return state.lists.isLoaded;
        },

        getModal: (state) => (): IModalType => {
            return state.modals;
        },

        getModalStatus:
            (state) =>
            (modalName: string): boolean => {
                if (!(modalName in state.modals)) {
                    return false;
                }

                return state.modals[modalName].isShow;
            },

        getResponse: (state) => (): IResponseDetail => {
            return state.response;
        },

        getRulesByKey:
            (state) =>
            (key: string): Object => {
                return state.forms.rules[key];
            },

        getSelOptLoaded: (state) => (): boolean => {
            return state.selopts.isLoaded;
        },

        getSelOptRefresh: (state) => (): boolean => {
            return state.selopts.isRefresh;
        },

        getUrlByKey:
            (state) =>
            (key: string): string => {
                return state.urls[key];
            }
    },

    actions: {
        clearComponentAction(name?: string): void {
            if (name) {
                this.components[name] = {};
            } else {
                this.components = {};
            }
        },

        clearFormError(): void {
            this.setFormError(false);
            this.setFormErrorMessage({});
        },

        clearError(): void {
            this.setResponse({
                action: '',
                status: false,
                message: '',
                data: null,
                error: null
            });
        },

        removeModal(modalName: string): void {
            delete this.modals[modalName];
        },

        resetResponse(): void {
            let store = this;

            setTimeout(function () {
                store.response = {
                    action: '',
                    status: false,
                    message: '',
                    data: null
                } as IResponseDetail;
            }, 500);
        },

        setComponentAction(name: string, action: Object): void {
            console.debug('components', this.components, name, 'action', action);

            if (!(name in this.components)) {
                let newComponent = {
                    [name]: action
                };

                this.components = {
                    ...this.components,
                    ...newComponent
                };
            } else {
                this.components[name] = action;
            }
        },

        setFormError(isError: boolean): void {
            this.forms.isError = isError;
        },

        setFormErrorMessage(error: Object): void {
            this.forms.error = error;
        },

        setFormID(id: string): void {
            this.forms.id = id;
        },

        setListDownloading(downloading: boolean): void {
            this.lists.isDownloaded = !downloading;
        },

        setListLoading(loading: boolean): void {
            this.lists.isLoaded = !loading;
        },

        setModal(modalName: string): void {
            let newModal = {
                [modalName]: {
                    isShow: false
                }
            };

            this.modals = {
                ...this.modals,
                ...newModal
            };
        },

        setResponse(response: IResponseDetail): void {
            this.response = response;
        },

        setRules(key: string, rules: Object): void {
            this.forms.rules[key] = rules;
        },

        setSelOptRefresh(refresh: boolean): void {
            this.selopts.isRefresh = refresh;
        },

        setSelOptLoaded(loaded: boolean): void {
            this.selopts.isLoaded = loaded;
        },

        setUrl(key: string, url: string): void {
            this.urls[key] = url;
        },

        setUrls(urls: IKeyString): void {
            this.urls = urls;
        },

        toggleModal(modalName: string, isShow: boolean): void {
            this.modals[modalName].isShow = isShow;
        }
    }
});
